/**
 * SSE frame parser. Given a fetch Response's body, yields `{ event, data }`
 * pairs as they arrive. Handles CRLF + LF separators, malformed frames
 * (silently skipped), and aborts cleanly when the abort signal fires.
 *
 * Frames follow the shape the chat ingress emits:
 *   event: token
 *   data: {"text": "hello"}
 *
 *   event: done
 *   data: {"response": "...", "conversationId": "..."}
 */

export interface SseFrame {
  event: string;
  data: unknown;
}

export async function* parseSseStream(body: ReadableStream<Uint8Array>): AsyncGenerator<SseFrame> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      // Normalise CRLF to LF so the split works identically whether the
      // origin proxied through a Windows-flavoured intermediary or not.
      buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n');
      const parts = buffer.split('\n\n');
      buffer = parts.pop() ?? '';
      for (const part of parts) {
        const frame = parseFrame(part);
        if (frame) yield frame;
      }
    }
    // Flush any trailing frame that didn't end with a blank line.
    if (buffer.trim().length > 0) {
      const frame = parseFrame(buffer);
      if (frame) yield frame;
    }
  } finally {
    try { reader.releaseLock(); } catch { /* already released */ }
  }
}

function parseFrame(raw: string): SseFrame | null {
  const eventMatch = raw.match(/^event:\s*(.+)$/m);
  const dataMatch = raw.match(/^data:\s*([\s\S]+)$/m);
  if (!eventMatch || !dataMatch) return null;
  let data: unknown;
  try { data = JSON.parse(dataMatch[1]); } catch { return null; }
  return { event: eventMatch[1].trim(), data };
}
